
import React, { useState, useEffect } from 'react';
import { useFormik } from "formik";
import { string } from "yup";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { styled } from '@mui/system';
import { useTranslation } from "react-i18next";
import { Divider, IconButton, Button } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { TextField } from '@mui/material';
import Switch from '@mui/material/Switch';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Radio from '@mui/material/Radio';
import { RadioGroup } from '@mui/material';
import { Spinner } from "reactstrap";
import toast, { Toaster } from 'react-hot-toast';
import { AiFillCloseSquare } from 'react-icons/ai';
import { makeStyles } from '@mui/styles';
import AnalyticsModal from 'components/Modal/AnalyticsModal';
import { CustomTextField } from 'elements';
import styles from "pages/AnalyticDashboard/styles"; // same look/feel as the single-bucket exporter
import { api_settings } from "../../redux/services/api";
import {
  exportMultiBucketAnalytics,
  scheduledMultiBucketReportDelivery,
  getAllMultiPOS,
} from "../../redux/actions/analytics/analyticsActions";

// Same 5-year-back / no-future bound the rest of the analytics section uses
// (Dashboard.js, CombinedAnalyticsDashboard.js) — this modal's own custom
// date fields had no bound at all before, so a user could pick any date,
// past or future, with nothing stopping them.
const EXPORTER_MAX_DATE = () => dayjs();
const EXPORTER_MIN_DATE = () => dayjs().subtract(5, 'year');

const useStyles = makeStyles(styles);

const ExporterButton = styled('div')(() => ({
  background: '#c8102e',
  padding: 10,
  borderRadius: 5,
  flex: 'none',
  fontFamily: 'Roboto',
  cursor: 'pointer',
  color: '#fff',
  fontSize: 12,
  float: 'right',
  marginBottom: 13,
  marginLeft: 12,
  "&:hover": {
    background: "#eb314f",
    boxShadow: "0 8px 16px 0 rgba(0,0,0,0.2)"
  },
}));

// The six report sections the backend can produce, same set the
// single-bucket scheduled-report modal offers (minus Annual Sales Trend,
// which only applies when date_type === "yearly" on that page too — kept
// out here for the same reason, to keep this file's first pass simple;
// add it back the same way if/when needed).
const REPORT_TYPES = [
  { key: "sales_volume", labelKey: "analyticsExporter.SalesVolume" },
  { key: "top_sub_product", labelKey: "analyticsExporter.TopTenSubproducts" },
  { key: "top_product", labelKey: "analyticsExporter.TopTenProducts" },
  { key: "top_ten_colors", labelKey: "analyticsExporter.TopTenColors" },
  { key: "colorant_consumption", labelKey: "analyticsExporter.ColorantConsumption" },
  { key: "top_base", labelKey: "analyticsExporter.TopTenBases" },
  // NEW — same per-store colorant matrix report the single-bucket exporter
  // (AnalyticDashboard/analyticsExporter.js) offers; the backend task is
  // already bucket-agnostic (schedule_report_multi loops it once per
  // selected bucket), this was just missing from this form's option list.
  // alwaysAvailable: true because, unlike the rows above, this report has
  // no corresponding live dashboard widget to derive an anySuccess flag
  // from — it's only ever produced as an emailed attachment — so it can't
  // be gated by `availableReports` the way the others are.
  { key: "colorant_consumption_per_store", labelKey: "analyticsExporter.ColorantConsumptionPerStore", alwaysAvailable: true },
];

/**
 * Exporter + scheduled-report-delivery button pair for the "Combined
 * Analysis" tab of the multi-bucket modal.
 *
 * @param {string[]} codeList - the selected bucket codes, e.g. ["B1","B2"]
 * @param {object} availableReports - { sales_volume: bool, top_product: bool, ... }
 *        computed by CombinedAnalyticsDashboard.js from each widget's
 *        `anySuccess` flag. A report type whose flag is false means NONE
 *        of the selected buckets returned data for it — its checkbox is
 *        disabled and it is skipped, never sent to the backend.
 */
export default function MultiBucketAnalyticsExporter({ codeList, availableReports = {}, defaultDateType = 'lastyear' }) {
  const { t } = useTranslation('common');
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    createSuccess,
    createError,
    exportLoading,
    posSuccess,
    posLoading,
    ccBucketAnalyticsScheduledReportDeliverySuccess,
    ccBucketAnalyticsScheduledReportDeliveryLoading,
    ccBucketAnalyticsScheduledReportDeliveryError,
  } = useSelector((state) => state.analytics.analytics);

  const [open, setOpen] = useState(false);
  const [scheduleModal, setScheduleModal] = useState(false);
  const bucketCodes = (codeList || []).join(',');

  /* ------------------------------------------------------------------ */
  /* POS picker — union of POS names across every selected bucket        */
  /* ------------------------------------------------------------------ */
  const [posList, setPosList] = useState([]);
  const [posSelectAll, setPosSelectAll] = useState(true);
  const [posSearchTerm, setPosSearchTerm] = useState("");

  // FIX (testing observation: POS list not restored after clearing the
  // search) — remembers each store's tick state by name, so when the list is
  // re-fetched (search cleared, or a new search run) stores the user had
  // unticked stay unticked instead of every store coming back ticked while
  // "Select all POS" shows unticked. Reset whenever a modal is (re)opened.
  const posCheckedRef = React.useRef({});
  useEffect(() => {
    posList.forEach((p) => { posCheckedRef.current[p.pos_name] = !!p.checked; });
  }, [posList]);

  useEffect(() => {
    if (posSuccess?.data) {
      setPosList(posSuccess.data.map((item) => ({
        ...item,
        checked: posCheckedRef.current[item.pos_name] !== undefined
          ? posCheckedRef.current[item.pos_name]
          : true,
      })));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posSuccess]);

  const fetchMultiPos = (search) => {
    if (codeList && codeList.length > 0) {
      dispatch(getAllMultiPOS({ codes: codeList, search: search || "" }));
    }
  };

  const handlePosSearch = () => {
    cancelPendingPosSearch();
    fetchMultiPos(posSearchTerm);
  };

  // FIX (testing observation: POS search) — live search:
  //  - typing searches automatically after a short pause (300 ms), so there
  //    is no need to click Search (the Search button still works exactly as
  //    before and searches immediately);
  //  - emptying the box (Backspace/Delete/clear) resets straight away to the
  //    full store list;
  //  - Enter in the box searches instead of submitting the Export/Schedule
  //    form by accident.
  const posSearchTimerRef = React.useRef(null);
  const cancelPendingPosSearch = () => {
    if (posSearchTimerRef.current) {
      clearTimeout(posSearchTimerRef.current);
      posSearchTimerRef.current = null;
    }
  };

  const handlePosSearchTermChange = (e) => {
    const value = e.target.value;
    setPosSearchTerm(value);
    cancelPendingPosSearch();
    if (value.trim() === "") {
      fetchMultiPos("");
    } else {
      posSearchTimerRef.current = setTimeout(() => {
        posSearchTimerRef.current = null;
        fetchMultiPos(value.trim());
      }, 300);
    }
  };

  const handlePosSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      cancelPendingPosSearch();
      fetchMultiPos(e.target.value.trim());
    }
  };

  // don't leave a pending search behind if the component goes away
  useEffect(() => cancelPendingPosSearch, []);

  const onChangeCheck = (item, index) => () => {
    setPosList((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], checked: !next[index].checked };
      const allChecked = next.every((p) => p.checked);
      setPosSelectAll(allChecked);
      return next;
    });
  };

  const checkSelectAll = () => {
    setPosSelectAll((prevAll) => {
      const nextAll = !prevAll;
      setPosList((prev) => prev.map((p) => ({ ...p, checked: nextAll })));
      return nextAll;
    });
  };

  /* ------------------------------------------------------------------ */
  /* On-demand export                                                    */
  /* ------------------------------------------------------------------ */
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { startdate: "", enddate: "", date_type_analytics: defaultDateType },
    validationSchema: Yup.object({
      startdate: string().when("date_type_analytics", {
        is: "custom",
        then: string()
          .required(t('analyticsExporter.StartDateisrequired'))
          .test('not-future', t('analyticsExporterModal.dateNotFuture', 'Date cannot be in the future'),
            (v) => !v || new Date(v) <= new Date())
          .test('within-5-years', t('analyticsExporterModal.dateWithin5Years', 'Date cannot be more than 5 years in the past'),
            (v) => !v || new Date(v) >= new Date(new Date().setFullYear(new Date().getFullYear() - 5))),
      }),
      enddate: string().when("date_type_analytics", {
        is: "custom",
        then: string()
          .required(t('analyticsExporter.Enddateisrequired'))
          .test('not-future', t('analyticsExporterModal.dateNotFuture', 'Date cannot be in the future'),
            (v) => !v || new Date(v) <= new Date())
          .test('not-before-start', t('analyticsExporterModal.endBeforeStart', 'End date cannot be before start date'),
            function (v) { return !v || !this.parent.startdate || new Date(v) >= new Date(this.parent.startdate); }),
      }),
    }),
    onSubmit: (values) => {
      const poslist = posList.filter((p) => p.checked).map((p) => p.pos_name);
      dispatch(exportMultiBucketAnalytics({ ...values, bucketCodes, posSelectAll, poslist }));
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      posCheckedRef.current = {};
      cancelPendingPosSearch();
      setPosList([]);
      setPosSelectAll(true);
      setPosSearchTerm("");
      fetchMultiPos("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // NEW — the schedule form's own store picker (added below in
  // scheduleContent) needs posList populated the same way the export
  // form's does; before this, only opening Export ever fetched it, so
  // opening Schedule directly left the store picker empty (same class of
  // bug already fixed once for the single-bucket exporter's
  // scheduledBtnClick — see that file's comment).
  useEffect(() => {
    if (scheduleModal) {
      posCheckedRef.current = {};
      cancelPendingPosSearch();
      setPosList([]);
      setPosSelectAll(true);
      setPosSearchTerm("");
      fetchMultiPos("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleModal]);

  useEffect(() => {
    if (createSuccess?.status === "success") {
      const downloadLink = document.createElement('a');
      downloadLink.href = api_settings.url_base + createSuccess.data.url;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      dispatch({ type: "DOWNLOAD_RESET", payload: null });
      setOpen(false);
    }
    if (createError) {
      dispatch({ type: "REQUEST_CREATE_ANALYTICS_FAILED", payload: null });
      toast.error(
        t(createError?.data ? createError?.data?.message : 'errorBlock.somethingWentWrong'),
        { duration: 8000, position: 'top-right', style: { fontSize: 20, fontFamily: 'Roboto' } }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSuccess, createError]);

  /* ------------------------------------------------------------------ */
  /* Scheduled report delivery                                           */
  /* ------------------------------------------------------------------ */
  const availableKeys = REPORT_TYPES.filter((r) => r.alwaysAvailable || availableReports[r.key]).map((r) => r.key);
  const [reportData, setReportData] = useState(availableKeys);
  const [checkBoxError, setCheckboxError] = useState("");

  useEffect(() => {
    // If the set of buckets/data changes (e.g. a bucket is added/removed,
    // or a section that had no data now does), drop anything checked that
    // is no longer backed by data — this IS the "skip missing data" rule
    // applied to the schedule form itself, not just the dashboard widgets.
    // alwaysAvailable report types (no dashboard widget to check) are never
    // dropped by this.
    setReportData((prev) => prev.filter((k) => {
      const def = REPORT_TYPES.find((r) => r.key === k);
      return def?.alwaysAvailable || availableReports[k];
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(availableReports)]);

  const scheduledFormik = useFormik({
    initialValues: { reportemail: "", date_type: "monthly", litreOrOrder: "order" },
    validationSchema: Yup.object({
      reportemail: string().required(t('signIn.Pleaseenteremail')).email(t('signIn.Invalidemail')),
    }),
    onSubmit: (values) => {
      if (reportData.length === 0) {
        setCheckboxError(t('analyticsExporter.reportsRequired'));
        return;
      }
      setCheckboxError("");
      dispatch(
        scheduledMultiBucketReportDelivery({
          report_choices: reportData,
          email: values.reportemail,
          bcc_email: values.reportemail,
          // ScheduledReportMultiSerializer requires bucket_codes as a JSON
          // list — codeList is already an array, don't join it into a
          // string (that was producing "Expected a list of items but got
          // type str").
          bucket_codes: codeList,
          date_type: values.date_type,
          identity_name: values.reportemail,
          // ScheduledReportMultiSerializer also requires `type` — mirrors
          // the single-bucket exporter's litre/order switch, which this
          // form was missing entirely (that was producing
          // "type: This field is required").
          type: values.litreOrOrder,
          // NEW — store scoping for store-scoped reports (Colorant
          // Consumption per Store). Same convention as the single-bucket
          // exporter: empty array (posSelectAll checked, or the list
          // hasn't loaded/nothing picked yet) means "all stores in every
          // selected bucket".
          pos_names: posSelectAll ? [] : posList.filter((p) => p.checked).map((p) => p.pos_name),
        })
      );
    },
  });

  const scheduleModalClose = () => {
    scheduledFormik.resetForm();
    setCheckboxError("");
    setScheduleModal(false);
  };

  useEffect(() => {
    if (ccBucketAnalyticsScheduledReportDeliverySuccess?.status?.length > 0) {
      setScheduleModal(false);
      scheduledFormik.resetForm();
      toast.success(t('analyticsExporter.Reportsentsuccessfully'), {
        duration: 8000,
        position: 'top-right',
        style: { fontSize: 20, fontFamily: 'Roboto' },
      });
      dispatch({ type: "REQUEST_SCHEDULED_REPORT_DELIVERY_SUCCESS", payload: null });
    }
    if (ccBucketAnalyticsScheduledReportDeliveryError) {
      toast.error(t('errorBlock.somethingWentWrong'), {
        duration: 8000,
        position: 'top-right',
        style: { fontSize: 20, fontFamily: 'Roboto' },
      });
      dispatch({ type: "REQUEST_SCHEDULED_REPORT_DELIVERY_FAILED", payload: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccBucketAnalyticsScheduledReportDeliverySuccess, ccBucketAnalyticsScheduledReportDeliveryError]);

  const toggleReport = (key) => (e) => {
    setReportData((prev) => (e.target.checked ? [...prev, key] : prev.filter((k) => k !== key)));
  };

  /* ------------------------------------------------------------------ */
  /* Render                                                               */
  /* ------------------------------------------------------------------ */
  const exportContent = (
    <form onSubmit={formik.handleSubmit}>
      <div className={classes.head}>
        <div className={classes.text}>{t('analyticsExporter.AnalyticsExporter')}</div>
        <div className={classes.button}>
          <IconButton onClick={handleClose}>
            <AiFillCloseSquare color='#C8102E' size="30px" />
          </IconButton>
        </div>
      </div>
      <Divider />
      <div style={{ marginTop: 20 }}>
        <InputLabel className={classes.inputLabel}>{t('analyticsExporter.datetype')}</InputLabel>
        <RadioGroup name='date_type_analytics' defaultValue="lastyear" style={{ display: 'inline-block' }}>
          <FormControlLabel onChange={formik.handleChange} name="date_type_analytics" value="lastyear" control={<Radio style={{ color: "#C8102E" }} />} label={t('XdataAnalyticsDetail.lastyear')} />
          <FormControlLabel onChange={formik.handleChange} name="date_type_analytics" value="thisyear" control={<Radio style={{ color: "#C8102E" }} />} label={t('XdataAnalyticsDetail.thisyear')} />
          <FormControlLabel onChange={formik.handleChange} name="date_type_analytics" value="lastmonth" control={<Radio style={{ color: "#C8102E" }} />} label={t('XdataAnalyticsDetail.lastmonth')} />
          <FormControlLabel onChange={formik.handleChange} name="date_type_analytics" value="thismonth" control={<Radio style={{ color: "#C8102E" }} />} label={t('XdataAnalyticsDetail.thismonth')} />
          <FormControlLabel onChange={formik.handleChange} name="date_type_analytics" value="custom" control={<Radio style={{ color: "#C8102E" }} />} label={t('XdataAnalyticsDetail.customDate')} />
        </RadioGroup>
      </div>

      {formik.values.date_type_analytics === "custom" && (
        <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
          <div style={{ flex: 1 }}>
            <InputLabel className={classes.inputLabel}>{t('analyticsExporterModal.startdate')}</InputLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={formik.values.startdate}
                minDate={EXPORTER_MIN_DATE()}
                maxDate={EXPORTER_MAX_DATE()}
                onChange={(value) => {
                  const d = value.$d;
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, '0');
                  const dd = String(d.getDate()).padStart(2, '0');
                  formik.setFieldValue('startdate', `${yyyy}-${mm}-${dd}`);
                }}
                renderInput={(params) => (
                  <TextField
                    fullWidth
                    variant="filled"
                    error={formik.touched.startdate && Boolean(formik.errors.startdate)}
                    helperText={formik.touched.startdate && formik.errors.startdate}
                    {...params}
                  />
                )}
              />
            </LocalizationProvider>
          </div>
          <div style={{ flex: 1 }}>
            <InputLabel className={classes.inputLabel}>{t('analyticsExporterModal.enddate')}</InputLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={formik.values.enddate}
                minDate={formik.values.startdate ? dayjs(formik.values.startdate) : EXPORTER_MIN_DATE()}
                maxDate={EXPORTER_MAX_DATE()}
                onChange={(value) => {
                  const d = value.$d;
                  const yyyy = d.getFullYear();
                  const mm = String(d.getMonth() + 1).padStart(2, '0');
                  const dd = String(d.getDate()).padStart(2, '0');
                  formik.setFieldValue('enddate', `${yyyy}-${mm}-${dd}`);
                }}
                renderInput={(params) => (
                  <TextField
                    fullWidth
                    variant="filled"
                    error={formik.touched.enddate && Boolean(formik.errors.enddate)}
                    helperText={formik.touched.enddate && formik.errors.enddate}
                    {...params}
                  />
                )}
              />
            </LocalizationProvider>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <InputLabel className={classes.inputLabel}>
          {t('analyticsExporterModal.posName', 'POS Name')}
        </InputLabel>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={t('analyticsExporterModal.searchPOS', 'Search POS')}
            value={posSearchTerm}
            onChange={handlePosSearchTermChange}
            onKeyDown={handlePosSearchKeyDown}
            InputProps={{
              endAdornment: (
                <IconButton size="small" onClick={handlePosSearch}>
                  <span style={{ fontSize: 12 }}>{t('analyticsExporterModal.search', 'Search')}</span>
                </IconButton>
              ),
            }}
          />
        </div>
        <FormControlLabel
          style={{ marginTop: 6 }}
          control={
            <Checkbox
              checked={posSelectAll}
              style={{ color: "#C8102E" }}
              onChange={checkSelectAll}
            />
          }
          label={t('analyticsExporterModal.selectAllPOS', 'Select all POS')}
        />
        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #E0E0E0', borderRadius: 4, padding: 6 }}>
          {posLoading ? (
            <div style={{ textAlign: 'center', padding: 10 }}>
              <Spinner size="sm">{t('common.Loading')}</Spinner>
            </div>
          ) : posList.length === 0 ? (
            <div style={{ fontFamily: 'Roboto', fontSize: 12, color: '#696969', padding: 6 }}>
              {t('analyticsExporterModal.noPOSFound', 'No POS found for the selected buckets')}
            </div>
          ) : (
            posList.map((item, index) => (
              <FormControlLabel
                key={item.pos_name + index}
                style={{ display: 'flex' }}
                control={
                  <Checkbox
                    checked={!!item.checked}
                    style={{ color: "#C8102E" }}
                    onChange={onChangeCheck(item, index)}
                  />
                }
                label={item.pos_name}
              />
            ))
          )}
        </div>
      </div>

      <div className={classes.button1} style={{ marginTop: 20 }}>
        <Button type="submit" variant="contained">
          {exportLoading ? <Spinner size="sm">{t('common.Loading')}</Spinner> : t('analyticsExporterModal.export')}
        </Button>
      </div>
    </form>
  );

  const scheduleContent = (
    <form onSubmit={scheduledFormik.handleSubmit}>
      <div className={classes.head}>
        <div className={classes.text}>{t('analyticsExporter.ScheduledReportDelivery')}</div>
        <div className={classes.button}>
          <IconButton onClick={scheduleModalClose}>
            <AiFillCloseSquare color='#C8102E' size="30px" />
          </IconButton>
        </div>
      </div>
      <Divider />
      <div style={{ marginTop: 20 }}>
        <InputLabel className={classes.inputLabel}>{t('analyticsExporter.datetype')}</InputLabel>
        <RadioGroup name='date_type' defaultValue="monthly" style={{ display: 'inline-block' }}>
          <FormControlLabel onChange={scheduledFormik.handleChange} name="date_type" value="monthly" control={<Radio style={{ color: "#C8102E" }} />} label={t('analyticsExporter.month')} />
          <FormControlLabel onChange={scheduledFormik.handleChange} name="date_type" value="daily" control={<Radio style={{ color: "#C8102E" }} />} label={t('analyticsExporter.day')} />
          <FormControlLabel onChange={scheduledFormik.handleChange} name="date_type" value="weekly" control={<Radio style={{ color: "#C8102E" }} />} label={t('analyticsExporter.week')} />
          <FormControlLabel onChange={scheduledFormik.handleChange} name="date_type" value="halfyearly" control={<Radio style={{ color: "#C8102E" }} />} label={t('analyticsExporter.halfyear')} />
          <FormControlLabel onChange={scheduledFormik.handleChange} name="date_type" value="yearly" control={<Radio style={{ color: "#C8102E" }} />} label={t('analyticsExporter.year')} />
        </RadioGroup>

        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center' }}>
          <span style={{ fontFamily: 'Roboto', fontSize: 13, marginRight: 8 }}>{t('analyticsExporter.liters', 'Liters')}</span>
          <Switch
            checked={scheduledFormik.values.litreOrOrder === "order"}
            onChange={(event) => scheduledFormik.setFieldValue('litreOrOrder', event.target.checked ? "order" : "litre")}
            size="small"
            color="error"
          />
          <span style={{ fontFamily: 'Roboto', fontSize: 13, marginLeft: 8 }}>{t('analyticsExporter.orders', 'Orders')}</span>
        </div>

        <div style={{ marginTop: 10 }}>
          <CustomTextField
            name="reportemail"
            label={t('analyticsExporter.EmailId')}
            type="email"
            onChange={scheduledFormik.handleChange}
            onBlur={scheduledFormik.handleBlur}
            value={scheduledFormik.values.reportemail}
            error={scheduledFormik.touched.reportemail && Boolean(scheduledFormik.errors.reportemail)}
            helperText={scheduledFormik.touched.reportemail && scheduledFormik.errors.reportemail}
            labelColor="#000"
            labelTextSize={16}
            inputbackgroundcolor="#E8E8E8"
            inputtextcolor="#000"
            placeholderColor="#A5A5A5"
            helpertextcolor="#C8102E"
            height={56}
          />
        </div>

        <InputLabel className={classes.inputLabel} style={{ marginTop: 15 }}>
          {t('analyticsExporter.Reports')}
        </InputLabel>
        <div>
          {REPORT_TYPES.map((r) => {
            const isAvailable = r.alwaysAvailable || availableReports[r.key];
            return (
              <FormControlLabel
                key={r.key}
                control={
                  <Checkbox
                    checked={reportData.includes(r.key)}
                    disabled={!isAvailable}
                    style={{ color: "#C8102E" }}
                    onChange={toggleReport(r.key)}
                  />
                }
                label={
                  isAvailable
                    ? t(r.labelKey)
                    : `${t(r.labelKey)} (${t('MultiBucketAnalytics.noData')})`
                }
              />
            );
          })}
        </div>

        {checkBoxError !== "" && (
          <p style={{ color: '#c8102e', fontFamily: 'Roboto', fontSize: 12 }}>{checkBoxError}</p>
        )}

        {/* NEW — store picker for the scheduled report, union of POS names
            across every selected bucket (same posList/fetchMultiPos used by
            the Export form above). Needed so store-scoped reports like
            Colorant Consumption per Store can actually be scoped, instead
            of pos_names always being sent as "all stores". */}
        <InputLabel className={classes.inputLabel} style={{ marginTop: 15 }}>
          {t('analyticsExporterModal.posName', 'POS Name')}
        </InputLabel>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder={t('analyticsExporterModal.searchPOS', 'Search POS')}
            value={posSearchTerm}
            onChange={handlePosSearchTermChange}
            onKeyDown={handlePosSearchKeyDown}
            InputProps={{
              endAdornment: (
                <IconButton size="small" onClick={handlePosSearch}>
                  <span style={{ fontSize: 12 }}>{t('analyticsExporterModal.search', 'Search')}</span>
                </IconButton>
              ),
            }}
          />
        </div>
        <FormControlLabel
          style={{ marginTop: 6 }}
          control={
            <Checkbox
              checked={posSelectAll}
              style={{ color: "#C8102E" }}
              onChange={checkSelectAll}
            />
          }
          label={t('analyticsExporterModal.selectAllPOS', 'Select all POS')}
        />
        <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #E0E0E0', borderRadius: 4, padding: 6 }}>
          {posLoading ? (
            <div style={{ textAlign: 'center', padding: 10 }}>
              <Spinner size="sm">{t('common.Loading')}</Spinner>
            </div>
          ) : posList.length === 0 ? (
            <div style={{ fontFamily: 'Roboto', fontSize: 12, color: '#696969', padding: 6 }}>
              {t('analyticsExporterModal.noPOSFound', 'No POS found for the selected buckets')}
            </div>
          ) : (
            posList.map((item, index) => (
              <FormControlLabel
                key={item.pos_name + index}
                style={{ display: 'flex' }}
                control={
                  <Checkbox
                    checked={!!item.checked}
                    style={{ color: "#C8102E" }}
                    onChange={onChangeCheck(item, index)}
                  />
                }
                label={item.pos_name}
              />
            ))
          )}
        </div>

        <div className={classes.buttons}>
          <Button className='appBtnCancel' size="small" onClick={scheduleModalClose}>
            {t('analyticsExporter.Cancel')}
          </Button>
          <div className={classes.sub}>
            <Button className='appBtn' type="submit" size="medium">
              {ccBucketAnalyticsScheduledReportDeliveryLoading
                ? <Spinner size="sm">{t('common.Loading')}</Spinner>
                : t('analyticsExporter.Schedule')}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );

  return (
    <>
      <Toaster />
      <ExporterButton onClick={() => setOpen(true)}>
        {t('analyticsExporter.AnalyticsExporter')}
      </ExporterButton>
      <ExporterButton onClick={() => setScheduleModal(true)}>
        {t('analyticsExporter.ScheduledReportDelivery')}
      </ExporterButton>

      <AnalyticsModal handleclose={handleClose} open={open} content={exportContent} />
      <AnalyticsModal handleclose={scheduleModalClose} open={scheduleModal} content={scheduleContent} />
    </>
  );
}